import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

function useReveal(delay = 0) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => el.classList.add('visible'), delay);
          obs.unobserve(el);
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -50px 0px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [delay]);
  return ref;
}

export default function BuyingPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const inspectionHeading = useReveal(0);
  const inspCard0 = useReveal(0);
  const inspCard1 = useReveal(120);
  const inspCard2 = useReveal(240);
  const inspRefs = [inspCard0, inspCard1, inspCard2];
  const heroImg = useReveal(200);
  const ftbImg = useReveal(0);
  const ftbText = useReveal(100);
  const processHeading = useReveal(0);
  const step0 = useReveal(0);
  const step1 = useReveal(100);
  const step2 = useReveal(200);
  const step3 = useReveal(300);
  const step4 = useReveal(400);
  const stepRefs = [step0, step1, step2, step3, step4];
  const faqHeading = useReveal(0);
  const faq0 = useReveal(0);
  const faq1 = useReveal(60);
  const faq2 = useReveal(120);
  const faq3 = useReveal(180);
  const faq4 = useReveal(240);
  const faq5 = useReveal(300);
  const faqRefs = [faq0, faq1, faq2, faq3, faq4, faq5];
  const ctaBlock = useReveal(0);

  const faqs = [
    { question: "What's the first step in buying a home?", answer: "The first step is getting pre-approved for a mortgage. This tells you how much you can afford and shows sellers you're a serious buyer. I can connect you with trusted lenders who will walk you through the process." },
    { question: "How much do I need for a down payment?", answer: "It depends on your loan type. Conventional loans typically require 3-20% down, FHA loans require as little as 3.5%, and VA loans (for veterans) often require 0% down. We'll discuss your options and find what works best for your situation." },
    { question: "What should I look for during showings?", answer: "Beyond the aesthetics, I'll help you evaluate the home's systems — foundation, roof, HVAC, electrical, and plumbing. My inspection background means I can spot potential issues early, saving you from costly surprises later." },
    { question: "How do I know if I'm getting a good deal?", answer: "I'll provide a comparative market analysis showing recent sales of similar homes in the area. Combined with my assessment of the home's condition, you'll have a clear picture of whether the price is fair." },
    { question: "What happens if the inspection finds problems?", answer: "We can negotiate with the seller to have repairs made, request a price reduction, or ask for a credit at closing. My inspection knowledge helps us prioritize what's truly important versus minor issues." },
    { question: "How long does the buying process take?", answer: "From offer acceptance to closing typically takes 30-45 days, though it can vary. I'll keep you informed throughout and coordinate with all parties to ensure a smooth, timely closing." },
  ];

  const inspCards = [
    { icon: 'ri-eye-line', title: 'Spot Issues Early', desc: "I'll point out red flags during showings, not after you're under contract. This saves time and helps you make informed decisions." },
    { icon: 'ri-scales-line', title: 'Negotiate Smarter', desc: "Know what repairs to request and what's actually worth worrying about. My expertise helps you negotiate from a position of knowledge." },
    { icon: 'ri-money-dollar-circle-line', title: 'Save Money', desc: "Avoid homes with hidden problems or budget for repairs with confidence. Either way, you'll make a smarter investment." },
  ];

  const steps = [
    { num: '1', title: 'Connect', desc: "We'll talk about your goals, budget, and timeline." },
    { num: '2', title: 'Search', desc: "I'll help you find homes that fit your needs." },
    { num: '3', title: 'Tour', desc: "We'll walk through properties with inspection-level insights." },
    { num: '4', title: 'Offer', desc: "I'll guide you through negotiations and paperwork." },
    { num: '5', title: 'Close', desc: "You get the keys, and I'm still here if you need me." },
  ];

  return (
    <div className="min-h-screen bg-[#FAF9F7] page-enter">

      {/* Hero */}
      <section className="pt-28 sm:pt-32 pb-14 sm:pb-20 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-10 sm:gap-14 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#C8923A] mb-4 animate-fade-in delay-100 font-medium">Buyer Services</p>
              <h1 className="font-cormorant text-4xl sm:text-6xl lg:text-7xl font-light text-[#111111] mb-6 animate-fade-in-up delay-200 leading-tight">
                Buy With<br /><em className="not-italic font-semibold">Confidence.</em>
              </h1>
              <p className="text-[#555] mb-8 leading-relaxed animate-fade-in-up delay-300">
                From first-time buyers to seasoned homeowners, I'll help you find the right home and avoid costly surprises with inspection-level expertise at every step.
              </p>
              <Link to="/contact" className="inline-block px-7 sm:px-8 py-3.5 sm:py-4 bg-[#1C3A6B] text-white text-sm font-semibold rounded-md hover:bg-[#152E55] transition-all whitespace-nowrap hover-lift animate-fade-in-up delay-400">
                Start Your Home Search
              </Link>
            </div>
            <div ref={heroImg} className="reveal from-right rounded-lg overflow-hidden">
              <img src="https://static.readdy.ai/image/577c45d6defa6ac02f4bf39c73856fa0/e95f43983084f7deef808fa9b2a569ce.jpeg" alt="Happy homebuyers with keys" className="w-full h-[300px] sm:h-[400px] lg:h-[500px] object-cover object-top" />
            </div>
          </div>
        </div>
      </section>

      {/* Inspection Advantage */}
      <section className="py-14 sm:py-20 bg-[#FAF9F7]">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
          <div ref={inspectionHeading} className="reveal text-center mb-10 sm:mb-14">
            <p className="text-xs uppercase tracking-[0.3em] text-[#C8923A] mb-4 font-medium">Why It Matters</p>
            <h2 className="font-cormorant text-4xl sm:text-5xl font-light text-[#111111] mb-4">The Inspection Advantage</h2>
            <p className="text-[#666] max-w-2xl mx-auto leading-relaxed">Most agents can show you a house. I can show you what's behind the walls. Three years as a certified home inspector mean you'll get insights on foundations, HVAC, electrical, plumbing, and roofing that most buyers never hear until it's too late.</p>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
            {inspCards.map((item, i) => (
              <div key={i} ref={inspRefs[i]} className="reveal scale bg-white rounded-lg p-7 sm:p-8 text-center hover-lift border border-gray-100">
                <div className="w-14 h-14 mx-auto mb-6 flex items-center justify-center">
                  <i className={`${item.icon} text-4xl text-[#1C3A6B]`}></i>
                </div>
                <h3 className="font-cormorant text-2xl font-semibold text-[#111111] mb-3">{item.title}</h3>
                <div className="w-8 h-0.5 bg-[#C8923A] mx-auto mb-4"></div>
                <p className="text-sm text-[#666] leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* First-Time Buyer */}
      <section className="py-14 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
          <div className="grid lg:grid-cols-5 gap-10 sm:gap-14 items-center">
            <div ref={ftbImg} className="reveal from-left lg:col-span-2 rounded-lg overflow-hidden">
              <img src="https://static.readdy.ai/image/577c45d6defa6ac02f4bf39c73856fa0/9ddc63dad71f7ca5e35a8d944cbead1e.jpeg" alt="Kendrick with first-time buyers" className="w-full h-[300px] sm:h-[400px] lg:h-[500px] object-cover object-top" />
            </div>
            <div ref={ftbText} className="reveal from-right lg:col-span-3">
              <p className="text-xs uppercase tracking-[0.3em] text-[#C8923A] mb-4 font-medium">First-Time Buyers</p>
              <h2 className="font-cormorant text-3xl sm:text-5xl font-light text-[#111111] mb-6 leading-tight">First-Time Buyer?<br /><em className="not-italic font-semibold">I've Got You.</em></h2>
              <div className="w-10 h-0.5 bg-[#C8923A] mb-6"></div>
              <p className="text-[#555] mb-8 leading-relaxed">Buying your first home should be exciting, not overwhelming. I'll walk you through every step, answer every question, and make sure you understand what you're getting into.</p>
              <div className="space-y-3">
                {[
                  { bold: 'Pre-approval guidance', rest: '– I\'ll connect you with trusted lenders' },
                  { bold: 'Home search strategy', rest: '– We\'ll find homes that fit your needs and budget' },
                  { bold: 'Showing walkthroughs', rest: '– I\'ll point out what to look for and what to avoid' },
                  { bold: 'Offer and negotiation support', rest: '– I\'ll help you craft competitive offers' },
                  { bold: 'Closing coordination', rest: '– I\'ll manage the details so you can focus on excitement' },
                  { bold: 'Post-purchase follow-up', rest: '– I\'m here even after you get the keys' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-lg text-[#C8923A] mt-0.5 flex-shrink-0"></i>
                    <span className="text-sm text-[#444]"><strong>{item.bold}</strong> {item.rest}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-14 sm:py-20 bg-[#FAF9F7]">
        <div className="max-w-5xl mx-auto px-5 sm:px-6">
          <div ref={processHeading} className="reveal text-center mb-10 sm:mb-12">
            <p className="text-xs uppercase tracking-[0.3em] text-[#C8923A] mb-4 font-medium">The Process</p>
            <h2 className="font-cormorant text-4xl sm:text-5xl font-light text-[#111111]">How It Works</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-5 sm:gap-6">
            {steps.map((step, i) => (
              <div key={i} ref={stepRefs[i]} className="reveal text-center">
                <div className="w-12 h-12 sm:w-14 sm:h-14 mx-auto mb-3 sm:mb-4 bg-[#1C3A6B] text-white rounded-full flex items-center justify-center font-cormorant text-xl sm:text-2xl font-semibold">
                  {step.num}
                </div>
                <h3 className="font-semibold text-[#111111] mb-1 text-sm sm:text-base">{step.title}</h3>
                <p className="text-xs text-[#666]">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-5 sm:px-6">
          <div ref={faqHeading} className="reveal text-center mb-10 sm:mb-12">
            <p className="text-xs uppercase tracking-[0.3em] text-[#C8923A] mb-4 font-medium">Questions</p>
            <h2 className="font-cormorant text-4xl sm:text-5xl font-light text-[#111111]">Frequently Asked</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <div key={index} ref={faqRefs[index]} className="reveal border border-gray-200 rounded-lg overflow-hidden bg-white">
                <button onClick={() => setOpenFaq(openFaq === index ? null : index)} className="w-full px-5 sm:px-6 py-4 sm:py-5 flex items-center justify-between text-left hover:bg-[#FAF9F7] transition-colors cursor-pointer">
                  <span className="font-medium text-[#1A1A1A] pr-4 text-sm">{faq.question}</span>
                  <i className={`ri-arrow-${openFaq === index ? 'up' : 'down'}-s-line text-xl text-[#C8923A] flex-shrink-0 transition-transform duration-300`}></i>
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${openFaq === index ? 'max-h-48 opacity-100' : 'max-h-0 opacity-0'}`}>
                  <div className="px-5 sm:px-6 pb-5 border-t border-gray-100">
                    <p className="text-sm text-[#666] leading-relaxed pt-4">{faq.answer}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 sm:py-20 bg-[#111111]">
        <div ref={ctaBlock} className="reveal max-w-4xl mx-auto px-5 sm:px-6 text-center">
          <h2 className="font-cormorant text-4xl sm:text-5xl font-light text-white mb-6">Ready to Start Looking?</h2>
          <p className="text-white/70 mb-8 sm:mb-10">Let's find the right home for you.</p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <a href="tel:+15176629155" className="px-7 sm:px-8 py-3.5 sm:py-4 bg-[#C8923A] text-white font-semibold text-sm rounded-md hover:bg-[#B07B2A] transition-all whitespace-nowrap inline-flex items-center justify-center gap-2 hover-lift">
              <i className="ri-phone-line"></i> Call Kendrick
            </a>
            <Link to="/contact" className="px-7 sm:px-8 py-3.5 sm:py-4 border border-white/30 text-white font-semibold text-sm rounded-md hover:bg-white/10 transition-all whitespace-nowrap hover-lift">
              Ask About Buying
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
